import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { LogoMark } from "@/components/brand/Logo";
import { Star, Quote } from "lucide-react";

const Testimonial = ({ t }) => (
  <div className="ccs-card p-6 lg:p-7 ccs-fade-up" data-testid={`testimonial-${t.id}`}>
    <Quote className="w-7 h-7 text-[#C28A2E]" strokeWidth={1.5} />
    <div className="flex items-center gap-1 mt-3">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-4 h-4 ${n <= (t.star_rating || 0) ? "fill-[#D4A547] text-[#D4A547]" : "text-[#EBE3D2]"}`} strokeWidth={1.5} />
      ))}
    </div>
    {t.enjoyed_most && (
      <p className="text-[#2A1810] text-base leading-relaxed mt-4 italic">"{t.enjoyed_most}"</p>
    )}
    {t.what_changed && (
      <p className="text-[#5D3A1F] text-sm leading-relaxed mt-3">
        <span className="font-semibold">What changed:</span> {t.what_changed}
      </p>
    )}
    {t.facilitator_response && (
      <div className="mt-4 pt-4 border-t border-[#EBE3D2] bg-[#FDFBF5] -mx-6 -mb-6 px-6 pb-6 rounded-b-2xl">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Joylance responded</div>
        <p className="text-sm text-[#5D3A1F] mt-1.5 leading-relaxed">{t.facilitator_response}</p>
      </div>
    )}
    <div className="mt-5 pt-4 border-t border-[#EBE3D2] flex items-center justify-between">
      <div>
        <div className="font-display font-semibold text-[#2A1810]">{t.display_name}</div>
        {t.organisation && <div className="text-xs text-[#6B5944]">{t.organisation}</div>}
      </div>
      <div className="text-xs text-[#6B5944]">
        {(t.created_at || "").slice(0, 10)}
      </div>
    </div>
  </div>
);

const TestimonialWallPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/testimonials").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const avg = items.length
    ? (items.reduce((sum, t) => sum + (t.star_rating || 0), 0) / items.length).toFixed(1)
    : null;

  return (
    <div className="min-h-screen bg-[#FDFBF5]" data-testid="testimonial-wall">
      <header className="border-b border-[#EBE3D2] bg-[#FDFBF5]/85 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <LogoMark size={42} />
            <div className="leading-tight">
              <div className="font-display text-lg font-semibold text-[#2A1810]">The CCS Approach</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B5944] font-medium">Voices from our clients</div>
            </div>
          </Link>
          <Link to="/book" data-testid="wall-book-link" className="text-sm bg-[#5D3A1F] hover:bg-[#4A2E18] text-white px-4 py-2 rounded-xl transition">
            Book a Consultation
          </Link>
        </div>
      </header>

      <section className="ccs-grain border-b border-[#EBE3D2]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14 lg:py-20 text-center">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">Testimonials</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#2A1810] mt-4 leading-[1.05]">
            Real change, in their<br /><span className="text-[#C28A2E]">own words.</span>
          </h1>
          <p className="text-base lg:text-lg text-[#5D3A1F] mt-5 max-w-xl mx-auto leading-relaxed">
            Stories from teams who chose to <strong>Communicate</strong>, <strong>Collaborate</strong>, and <strong>Serve</strong> with intention.
          </p>
          {avg && (
            <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 rounded-full bg-white border border-[#EBE3D2]">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className={`w-5 h-5 ${n <= Math.round(avg) ? "fill-[#D4A547] text-[#D4A547]" : "text-[#EBE3D2]"}`} strokeWidth={1.5} />
                ))}
              </div>
              <div className="text-sm text-[#2A1810]">
                <strong>{avg}</strong> avg from <strong>{items.length}</strong> participants
              </div>
            </div>
          )}
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        {loading ? (
          <div className="text-center text-[#6B5944] py-12">Loading testimonials…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <Quote className="w-10 h-10 text-[#C28A2E] mx-auto mb-3" strokeWidth={1.5} />
            <div className="font-display text-xl text-[#2A1810]">Coming soon</div>
            <p className="text-[#6B5944] mt-2 max-w-md mx-auto">
              We're collecting feedback from our most recent programmes. Check back shortly.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((t) => <Testimonial key={t.id} t={t} />)}
          </div>
        )}
      </main>

      <footer className="py-10 text-center text-xs text-[#6B5944]">
        © {new Date().getFullYear()} The CCS Approach · Joylance Chapman-George ·{" "}
        <span className="text-[#C28A2E] font-semibold">Communicate · Collaborate · Serve</span>
      </footer>
    </div>
  );
};

export default TestimonialWallPage;
